import { useEffect, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';

export type PhoneVerificationStatus = 'entry' | 'submitting' | 'error';
export type PhoneVerificationErrorReason = 'invalid_code' | 'expired' | 'rate_limited' | 'unknown' | 'send_unavailable' | 'send_cooldown';

const ERROR_MESSAGES: Record<PhoneVerificationErrorReason, string> = {
  invalid_code: "That code isn't right. Check it and try again.",
  expired: 'That code has expired. Request a new one to continue.',
  rate_limited: 'You have made too many attempts. Request a new code or use a different number to continue.',
  send_cooldown: 'Please wait before requesting another code.',
  send_unavailable: "We couldn't send a verification code to this number. Please use a different number.",
  unknown: 'Something went wrong. Please try again.',
};

const TERMINAL_ERRORS = new Set<PhoneVerificationErrorReason>(['rate_limited', 'expired', 'send_unavailable']);

const COOLDOWN_SECONDS = 30;

const SUPPORT_MAILTO = 'mailto:info@sitemate.com?subject=Phone%20verification%20issue';

interface PhoneVerificationStepProps {
  status: PhoneVerificationStatus;
  errorReason?: PhoneVerificationErrorReason;
  maskedPhone: string;
  serverCooldownSeconds?: number;
  onVerify: (code: string) => void;
  onResend: () => void;
  onRequestCall: () => void;
  onUseDifferentNumber: () => void;
}

export function PhoneVerificationStep({
  status,
  errorReason,
  maskedPhone,
  serverCooldownSeconds = 0,
  onVerify,
  onResend,
  onRequestCall,
  onUseDifferentNumber,
}: PhoneVerificationStepProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [countdown, setCountdown] = useState(COOLDOWN_SECONDS);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // A 429 carries the server's remaining wait; seed the countdown with it instead of the default 30s.
  useEffect(() => {
    if (serverCooldownSeconds > 0) {
      setCountdown(serverCooldownSeconds);
    }
  }, [serverCooldownSeconds]);

  const code = digits.join('');
  const isComplete = code.length === 6 && digits.every((d) => d !== '');
  const isSubmitting = status === 'submitting';
  const isTerminalError = status === 'error' && errorReason !== undefined && TERMINAL_ERRORS.has(errorReason);
  const showError = status === 'error' && errorReason !== undefined;

  function handleChange(index: number, value: string) {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handleVerify() {
    if (!isComplete || isSubmitting || isTerminalError) return;
    onVerify(code);
  }

  function resetForResend() {
    setDigits(Array(6).fill(''));
    setCountdown(COOLDOWN_SECONDS);
    inputRefs.current[0]?.focus();
  }

  function handleResend() {
    resetForResend();
    onResend();
  }

  function handleRequestCall() {
    resetForResend();
    onRequestCall();
  }

  return (
    <div className="z-10 m-auto flex w-[500px] flex-col items-center gap-4 rounded bg-white p-[30px] shadow">
      <h2 className="text-center text-xl font-medium">Verify your phone number</h2>
      <p className="text-default-text text-center text-sm">
        Enter the 6-digit code we sent to <span className="font-medium">{maskedPhone}</span> to continue.
      </p>

      <div className="flex justify-center gap-3">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : undefined}
            maxLength={1}
            value={digit}
            disabled={isSubmitting}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            aria-label={`Digit ${i + 1} of 6`}
            className={`h-[60px] w-14 rounded border text-center text-lg focus:outline-none disabled:opacity-50 ${
              showError ? 'border-red-500 focus:border-red-500' : 'border-grey-3 focus:border-default-blue'
            }`}
          />
        ))}
      </div>

      {status === 'error' && errorReason && (
        <p className="text-secondary-red text-center text-xs" role="alert" aria-live="assertive" data-testid="verification-error">
          {ERROR_MESSAGES[errorReason]}
        </p>
      )}

      <p className="text-grey-6 text-center text-xs">
        Didn't receive a code?{' '}
        {countdown > 0 ? (
          <span className="text-grey-5">Resend in 0:{String(countdown).padStart(2, '0')}</span>
        ) : (
          <>
            <button type="button" className="text-default-blue font-medium" onClick={handleResend}>
              Resend code
            </button>
            <span className="text-grey-6"> or </span>
            <button type="button" className="text-default-blue font-medium" onClick={handleRequestCall}>
              Request a call
            </button>
          </>
        )}
      </p>

      <div className="flex gap-3">
        <Button
          size="lg"
          className="p-2.5 text-sm font-medium"
          type="button"
          loading={isSubmitting}
          disabled={!isComplete || isSubmitting || isTerminalError}
          onClick={handleVerify}>
          Verify code
        </Button>
        <button
          type="button"
          className="bg-grey-2 rounded p-2.5 text-sm font-medium hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onUseDifferentNumber}>
          Use a different number
        </button>
      </div>

      {showError && (
        <p className="text-grey-6 text-center text-xs">
          Still having trouble?{' '}
          <a className="text-default-blue font-medium" href={SUPPORT_MAILTO} data-testid="contact-support">
            Contact support
          </a>
        </p>
      )}
    </div>
  );
}
